import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  AccountCircle,
  Brightness4,
  Brightness7,
  Logout,
  ShoppingCart,
  LocalShipping,
  CheckCircle,
  Info,
  Settings,
} from '@mui/icons-material';
import { toggleTheme } from '../../redux/slices/themeSlice';
import { logout } from '../../redux/slices/authSlice';
import StoreSelector from '../../common/StoreSelector';
import notificationService from '../../services/notificationService';
import { hasPermission, getNotificationTargetPath } from '../../utils/permissions';

const Header = ({ onMenuClick }) => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const theme     = useTheme();
  const { t }     = useTranslation();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const { user }  = useSelector((state) => state.auth);
  const { mode }  = useSelector((state) => state.theme);

  const [anchorEl, setAnchorEl]               = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications]     = useState([]);
  const [unreadCount, setUnreadCount]         = useState(0);

  // Icon shown per backend notification `type` — falls back to a generic info icon
  const NOTIFICATION_ICONS = {
    ORDER_CREATED:                 <ShoppingCart color="secondary" />,
    ORDER_STATUS_CHANGED:          <ShoppingCart color="primary" />,
    ORDER_PAYMENT_STATUS_CHANGED:  <CheckCircle color="success" />,
    PURCHASE_ORDER_STATUS_CHANGED: <LocalShipping color="info" />,
  };
  const iconFor = (type) => NOTIFICATION_ICONS[type] || <Info color="action" />;

  const loadNotifications = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        notificationService.getNotifications(),
        notificationService.getUnreadCount(),
      ]);
      setNotifications(list);
      setUnreadCount(count);
    } catch {
      // Notification feed is best-effort — leave the bell at its last known state
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleThemeToggle   = () => dispatch(toggleTheme());
  const handleMenu          = (e) => setAnchorEl(e.currentTarget);
  const handleClose         = () => setAnchorEl(null);
  const handleNotifClick    = (e) => { setNotificationAnchor(e.currentTarget); loadNotifications(); };
  const handleNotifClose    = () => setNotificationAnchor(null);

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationService.markAllAsRead();
    } catch {
      loadNotifications(); // resync with the server if the request failed
    }
  };

  const handleViewAll = () => { handleNotifClose(); navigate('/notifications'); };

  // Click on a notification: mark it read, then — if it points at an entity
  // and the current user has the required permission — go to that entity's
  // details page. Otherwise just close the menu.
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      notificationService.markAsRead(notification.id).catch(() => {});
    }

    const targetPath = getNotificationTargetPath(notification);
    handleNotifClose();

    if (!targetPath) return;

    if (!hasPermission(user, notification.requiredPermission)) {
      toast.warning(t('header.noPermissionForNotification'));
      return;
    }

    navigate(targetPath);
  };

  const handleLogout = async () => {
    handleClose();
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backgroundColor: (t) => t.palette.background.paper,
        boxShadow: '0 1px 1px rgba(0,0,0,0.05)',
        borderBottom: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton color="inherit" edge="start" onClick={onMenuClick} sx={{ mr: 2, color: 'text.primary' }}>
            <MenuIcon />
          </IconButton>
        )}

        <Typography
          variant="h5"
          noWrap
          sx={{
            flexGrow: 1,
            fontWeight: 700,
            letterSpacing: 1,
            color: 'primary.main',
          }}
        >
          INVOTRIX
        </Typography>

        <Box sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>
          <StoreSelector size="small" />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Theme toggle */}
          <Tooltip title={mode === 'light' ? t('header.switchToDark') : t('header.switchToLight')}>
            <IconButton onClick={handleThemeToggle} sx={{ color: 'text.primary' }}>
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title={t('header.notifications')}>
            <IconButton onClick={handleNotifClick} sx={{ color: 'text.primary' }}>
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Account */}
          <Tooltip title={user?.name || user?.email || ''}>
            <IconButton onClick={handleMenu} sx={{ color: 'text.primary' }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Notifications panel ──────────────────────────── */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotifClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { width: 360, maxHeight: 480, mt: 1.5 } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>{t('header.notifications')}</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
                {t('header.markAllRead')}
              </Button>
            )}
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('header.noNotifications')}</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 360, overflow: 'auto' }}>
              {notifications.map((n, idx) => (
                <Box key={n.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(n)}
                    sx={{ py: 1.5, cursor: 'pointer', backgroundColor: n.read ? 'transparent' : 'action.hover' }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'transparent' }}>{iconFor(n.type)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      // ListItemText wraps `secondary` in its own <p> (via
                      // MUI's default secondaryTypographyProps.component).
                      // The inner <Typography variant="body2"> below also
                      // defaults to component="p" — nesting a <p> inside
                      // that wrapper <p>, which is invalid HTML and throws
                      // a hydration error. Forcing the wrapper to a <div>
                      // fixes it without changing how anything looks.
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Typography variant="body2" fontWeight={n.read ? 400 : 600}>
                          {/* Platform notification from CronixLabs */}
                          {n.referenceType === 'PLATFORM' && (
                            <Chip label="CronixLabs" size="small" color="primary" sx={{ fontSize: '0.6rem', height: 16, mr: 1 }} />
                          )}
                          {n.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>{n.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNowStrict(new Date(n.createdAt), { addSuffix: true, locale: fr })}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {idx < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button fullWidth onClick={handleViewAll} sx={{ textTransform: 'none' }}>
              {t('header.viewAllNotifications')}
            </Button>
          </Box>
        </Menu>

        {/* ── User menu ────────────────────────────────────── */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { mt: 1.5, minWidth: 200 } }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600}>{user?.name || 'User'}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{user?.email}</Typography>
            {user?.roleName && (
              <Chip label={user.roleDisplayName || user.roleName} size="small" color="secondary" sx={{ mt: 1 }} />
            )}
          </Box>
          <MenuItem onClick={() => { handleClose(); navigate('/profile'); }} sx={{ py: 1.5 }}>
            <AccountCircle sx={{ mr: 1.5, color: 'text.secondary' }} />
            {t('header.myProfile')}
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); navigate('/settings'); }} sx={{ py: 1.5 }}>
            <Settings sx={{ mr: 1.5, color: 'text.secondary' }} />
            {t('header.settings')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
            <Logout sx={{ mr: 1.5 }} />
            {t('header.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
